import React, { useEffect, useState } from "react";
import "./ProfilePage.css";
import { useDispatch, useSelector } from "react-redux";
import dummyProfile from "../../images/dummyProfile.jpg";
import CountrySelect from "../countrySelect";
import { useAlert } from "react-alert";
import api from "../../api";
import userAction from "../../redux/users/action";
import { Modal } from "react-bootstrap";
import OtpInput from "react-otp-input";
import { Eye, EyeOff } from "lucide-react";

const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20h4l10-10a2.8 2.8 0 1 0-4-4L4 16z" />
  </svg>
);
const IconLock = ({ color = "#4c3fbd" }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2.5" />
    <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
  </svg>
);
const IconEmail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="m3.5 7.5 8.5 6 8.5-6" />
  </svg>
);
const IconVerified = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#16a34a" style={{ flex: "none" }}>
    <path d="M12 2.5 14 4.6l2.9-.4.9 2.8 2.6 1.4-1 2.8 1 2.8-2.6 1.4-.9 2.8-2.9-.4L12 21.5 10 19.4l-2.9.4-.9-2.8L3.6 15.6l1-2.8-1-2.8L6.2 8.6l.9-2.8 2.9.4z" />
    <path d="m8.6 12.2 2.2 2.2 4.4-4.4" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconUser = () => (
  <svg width="76" height="76" viewBox="0 0 24 24" fill="#fff">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0z" />
  </svg>
);

const FIELD_SPECS = [
  { key: "name",    label: "Full name",       placeholder: "Your full name",     type: "text" },
  { key: "dob",     label: "Date of birth",   placeholder: "YYYY-MM-DD",         type: "date" },
  { key: "email",   label: "Email address",   placeholder: "you@example.com",    type: "email",   readOnly: true },
  { key: "country", label: "Country",         placeholder: "Country of residence",               isCountry: true },
  { key: "phone",   label: "Phone number",    placeholder: "+92 300 0000000",    type: "text" },
];

function calcStrength(data) {
  const keys = ["name", "dob", "email", "country", "phone"];
  const filled = keys.filter(k => String(data[k] || "").trim()).length;
  return { filled, pct: Math.round((filled / keys.length) * 100) };
}

const ProfilePage = () => {
  const userStore = useSelector((state) => state.userReducer);
  const alert = useAlert();
  const dispatch = useDispatch();
  const { setUser } = userAction;

  const [userData, setUserData] = useState({});
  const [inputData, setInputData] = useState({});
  const [openEdit, setOpenEdit] = useState(false);
  const [loader, setLoader] = useState(false);
  const [imgLoader, setImgLoader] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);

  useEffect(() => {
    setUserData(userStore?.users?.user || {});
    setInputData(userStore?.users?.user || {});
  }, [userStore?.users]);

  const onTextChange = (e) => {
    if (e?.label) {
      setInputData({ ...inputData, country: e.label });
    } else {
      setInputData({ ...inputData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async () => {
    if (!inputData?.name)    { alert.show("Please insert name"); return; }
    if (!inputData?.dob)     { alert.show("Please insert DOB"); return; }
    if (!inputData?.country) { alert.show("Please select country"); return; }
    setLoader(true);
    try {
      const res = await api.userProfileEdit({ ...inputData });
      if (res?.success) {
        let updated = { ...userStore?.users };
        updated.user = { ...updated.user, name: inputData.name, dob: inputData.dob, country: inputData.country, phone: inputData.phone };
        dispatch(setUser(updated));
        alert.show("Profile Updated Successfully");
        setOpenEdit(false);
      } else {
        alert.show("Something went wrong");
      }
    } catch {
      alert.show("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleImgUpload = async (e) => {
    setImgLoader(true);
    try {
      const formData = new FormData();
      formData.append("image", e.target.files[0], e.target.files[0].name);
      const res = await api.updateUserProfileImg(formData);
      if (res?.success) {
        let updated = { ...userStore?.users };
        updated.user = { ...updated.user, profileImg: res.profileImg };
        dispatch(setUser(updated));
      } else {
        alert.show("Image upload failed");
      }
    } catch {
      alert.show("Image upload failed");
    } finally {
      setImgLoader(false);
    }
  };

  const { filled, pct } = calcStrength(userData);

  const contactRows = [
    { tag: "@", label: "Email",           value: userData?.email || "—",     verified: true  },
    { tag: "#", label: "Phone number",    value: userData?.phone || "—",     verified: false },
    { tag: "~", label: "Account created", value: userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : "—", verified: false },
  ];

  return (
    <div className="pp-root">
      {/* Animated grid */}
      <div className="pp-grid" />
      <div className="pp-vignette" />
      <div className="pp-blob-left" />
      <div className="pp-blob-right" />

      <div className="pp-inner">

        {/* ── Header ── */}
        <div className="pp-header">
          <div className="pp-badge">
            <span className="pp-badge-dot" />
            <span className="pp-badge-text">Account verified</span>
          </div>
          <h1 className="pp-headline">
            <span className="pp-headline-text">Profile Details</span>
            <span className="pp-headline-shimmer">
              <span className="pp-headline-shimmer-bar" />
            </span>
          </h1>
          <p className="pp-subtitle">Manage your personal details and keep your account information up to date.</p>
        </div>

        {/* ── Two-column layout ── */}
        <div className="pp-grid-layout">

          {/* Left column */}
          <div className="pp-left">

            {/* Contact card */}
            <div className="pp-contact-card">
              <div className="pp-contact-header">
                <span className="pp-contact-header-sweep" />
                <IconEmail />
                Contact &amp; Account Details
              </div>
              {contactRows.map((row) => (
                <div className="pp-contact-row" key={row.label}>
                  <span className="pp-contact-tag">{row.tag}</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="pp-contact-label">{row.label}</div>
                    <div className="pp-contact-value">
                      {row.value}
                      {row.verified && <IconVerified />}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Profile strength */}
            <div className="pp-strength-card">
              <div className="pp-strength-header">
                <span className="pp-strength-label">Profile strength</span>
                <span className="pp-strength-pct">{pct}%</span>
              </div>
              <div className="pp-strength-bar">
                {[1,2,3,4,5].map((n) => (
                  <div className="pp-strength-seg" key={n}>
                    {n <= filled && <div className="pp-strength-seg-fill" />}
                  </div>
                ))}
              </div>
              <div className="pp-strength-hint">
                {pct < 100
                  ? "Add your date of birth, country and phone number to reach 100%."
                  : "Your profile is complete!"}
              </div>
            </div>
          </div>

          {/* Right: main profile card */}
          <div className="pp-main-card">

            {/* Hero banner */}
            <div className="pp-hero">
              <div className="pp-hero-grid" />
              <span className="pp-hero-sweep" />
              <div className="pp-hero-btns">
                <button
                  type="button"
                  className="pp-btn-glass"
                  onClick={() => setOpenEdit((v) => !v)}
                >
                  <IconEdit />
                  {openEdit ? "Editing" : "Edit Profile"}
                </button>
                <button
                  type="button"
                  className="pp-btn-white"
                  onClick={() => setShowPassModal(true)}
                >
                  <IconLock />
                  Update Password
                </button>
              </div>
            </div>

            {/* Card body */}
            <div className="pp-card-body">

              {/* Avatar */}
              <div className="pp-avatar-wrapper">
                <span className="pp-avatar-ring1" />
                <span className="pp-avatar-ring2" />
                <span className="pp-avatar-glow" />
                <div className="pp-avatar-circle">
                  {imgLoader ? (
                    <i className="fa fa-spinner fa-pulse" style={{ color: "#fff", fontSize: 28 }} />
                  ) : userData?.profileImg ? (
                    <img
                      src={`${process.env.REACT_APP_S3URL}/${userData.profileImg}`}
                      alt="avatar"
                    />
                  ) : (
                    <IconUser />
                  )}
                </div>
                <label className="pp-avatar-edit-btn" title="Change photo">
                  <IconEdit />
                  <input type="file" accept="image/*" onChange={handleImgUpload} />
                </label>
              </div>

              {/* Name + badge */}
              <div className="pp-name-row">
                <div className="pp-display-name">{userData?.name || "Unnamed member"}</div>
                <span className="pp-verified-pill">Verified member</span>
              </div>

              {/* Fields grid */}
              <div className="pp-fields-grid">
                {FIELD_SPECS.map((spec) => (
                  <div className="pp-field-card" key={spec.key}>
                    <span className="pp-field-accent" />
                    <div className="pp-field-label">{spec.label}</div>
                    {openEdit && !spec.readOnly ? (
                      spec.isCountry ? (
                        <div style={{ marginTop: 8 }}>
                          <CountrySelect onTextChange={onTextChange} />
                        </div>
                      ) : (
                        <input
                          className="pp-field-input"
                          type={spec.type}
                          name={spec.key}
                          value={inputData?.[spec.key] || ""}
                          onChange={onTextChange}
                          placeholder={spec.placeholder}
                        />
                      )
                    ) : (
                      <div className="pp-field-value">
                        {userData?.[spec.key] || "—"}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Save / Cancel */}
              {openEdit && (
                <div className="pp-edit-actions">
                  <button
                    type="button"
                    className="pp-btn-save"
                    onClick={handleSubmit}
                    disabled={loader}
                  >
                    {loader && <i className="fa fa-spinner fa-pulse" style={{ marginRight: 8 }} />}
                    Save changes
                  </button>
                  <button
                    type="button"
                    className="pp-btn-cancel"
                    onClick={() => { setOpenEdit(false); setInputData(userData); }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password modal */}
      <ChangePasswordModal
        show={showPassModal}
        onHide={() => setShowPassModal(false)}
        email={userData?.email}
      />
    </div>
  );
};

const ChangePasswordModal = ({ show, onHide, email }) => {
  const alert = useAlert();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [verifiedOtp, setVerifiedOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loader, setLoader] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const handleClose = () => {
    onHide();
    setStep(1); setOtp(""); setVerifiedOtp("");
    setPassword(""); setConfirmPassword("");
    setOtpSent(false); setShowPass(false); setShowConfirmPass(false);
  };

  const handleSendOtp = async () => {
    setLoader(true);
    try {
      const res = await api.sendOnlyOtp({ email });
      if (res?.success) { setOtpSent(true); alert.show("OTP sent to your email"); }
      else alert.show(res?.message || "Failed to send OTP");
    } catch { alert.show("Something went wrong"); }
    finally { setLoader(false); }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) return alert.show("Please enter 6-digit OTP");
    setLoader(true);
    try {
      const res = await api.isOtpVerified({ email, otp });
      if (res?.success) { setVerifiedOtp(otp); setStep(2); }
      else alert.show(res?.message || "Invalid OTP");
    } catch { alert.show("Something went wrong"); }
    finally { setLoader(false); }
  };

  const handleChangePassword = async () => {
    if (!password || !confirmPassword) return alert.show("Please fill all fields");
    if (password !== confirmPassword) return alert.show("Passwords do not match");
    if (password.length < 8) return alert.show("Password must be at least 8 characters");
    setLoader(true);
    try {
      const res = await api.changePassword({ otp: verifiedOtp, password, confirmPassword });
      if (res?.success) { alert.show("Password changed successfully"); handleClose(); }
      else alert.show(res?.message || "Something went wrong");
    } catch (err) { alert.show(err?.message || "Something went wrong"); }
    finally { setLoader(false); }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Body>
        <div className="otpModalMain">
          <div className="closeIcon" onClick={handleClose}>
            <i className="fa-solid fa-xmark" />
          </div>

          {step === 1 ? (
            <>
              <h2 className="title">Verify Your Identity</h2>
              <div className="desc">
                We will send a 6-digit code to <strong>{email}</strong>
              </div>
              {!otpSent ? (
                <div className="text-center mt-4">
                  <button onClick={handleSendOtp} className="themeBtn w-100" disabled={loader}>
                    {loader && <i className="fa fa-spinner fa-pulse mx-2" />}
                    Send OTP
                  </button>
                </div>
              ) : (
                <>
                  <div className="otpInputMain mt-3">
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      isInputNum={true}
                      separator={<span>-</span>}
                    />
                  </div>
                  <div className="text-center mt-3">
                    <button onClick={handleVerifyOtp} className="themeBtn w-100" disabled={loader}>
                      {loader && <i className="fa fa-spinner fa-pulse mx-2" />}
                      Verify OTP
                    </button>
                  </div>
                  <div className="text-center mt-2">
                    <small style={{ cursor: "pointer", textDecoration: "underline" }} onClick={handleSendOtp}>
                      Resend OTP
                    </small>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <h2 className="title">Set New Password</h2>
              <div className="desc">Enter your new password below</div>
              <div className="mt-3">
                <div style={{ position: "relative" }} className="mb-3">
                  <input
                    type={showPass ? "text" : "password"}
                    className="form-control"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <span
                    onClick={() => setShowPass(!showPass)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#888" }}
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPass ? "text" : "password"}
                    className="form-control"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <span
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#888" }}
                  >
                    {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
              </div>
              <div className="text-center mt-4">
                <button onClick={handleChangePassword} className="themeBtn w-100" disabled={loader}>
                  {loader && <i className="fa fa-spinner fa-pulse mx-2" />}
                  Save Password
                </button>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProfilePage;
